/*
 * Copyright (c) 2020.
 * This file is part of the Compo B2B application.
 * (c) Compo soft - Nikita Tropin <ntropin@yandex.ru>
 * For the full copyright and license information, please view the LICENSE
 * file that was distributed with this source code.
 */

import {Injectable, OnDestroy} from '@angular/core';
import {merge, MonoTypeOperatorFunction, Observable, Subject} from 'rxjs';
import {SubtaskLinkAction} from './subtask-link.action';
import {exhaustMap, filter, map, publishReplay, refCount, scan, startWith, tap} from 'rxjs/operators';
import {SubtaskLinkState} from './subtask-link.state';
import {SubtaskLinkService} from './subtask-link.service';
import {CrmIssueElastic} from 'compob2b-libs';

const defaultState: SubtaskLinkState = {
  items: [],
  loading: false
};

@Injectable({
  providedIn: 'root'
})
export class SubtaskLinkStore implements OnDestroy {

  actions$: Subject<SubtaskLinkAction> = new Subject<SubtaskLinkAction>();

  totalSpent = 0;
  totalEstimate = 0;

  load$: Observable<SubtaskLinkAction> =
    this.actions$.pipe(
      ofType('load'),
      exhaustMap((action: SubtaskLinkAction) => this.service.load(action.payload)),
      map(payload => ({
        type: 'load success', payload
      }))
    );

  store$: Observable<SubtaskLinkAction> =
    this.actions$.pipe(
      ofType('store'),
      exhaustMap((action: SubtaskLinkAction) => this.service.store(action.payload)),
      map(payload => ({
        type: 'store success', payload
      }))
    );

  /**
   * Dispatcher
   */
  dispatcher$: Observable<SubtaskLinkAction> =
    merge(this.actions$, this.load$, this.store$);

  /**
   * State Reducer
   */
  state$: Observable<SubtaskLinkState|SubtaskLinkAction> =
    this.dispatcher$.pipe(
      //@ts-ignore
      startWith(defaultState),
      //@ts-ignore
      scan((state: SubtaskLinkState, action: SubtaskLinkAction): SubtaskLinkState => {
        switch (action.type) {
          case 'load':
          case 'store':
            return {...state, loading: true};
          case 'load success':
            return {...state, items: action.payload, loading: false};
          case 'store success':
            const items = [];
            items.push(action.payload);
            return {
              ...state,
              items: [...state.items, ...items],
              loading: false
            };
          default:
            return state;
        }
      }),
      publishReplay(1),
      refCount()
    );

  items$: Observable<CrmIssueElastic[]> =
    this.state$.pipe(
      //@ts-ignore
      map((state: SubtaskLinkState) => {
        this.totalSpent = 0;
        this.totalEstimate = 0;

        return state.items;
      }),
      tap(issues => {
        issues.forEach(issue => {
          this.totalSpent += issue.spent;
          this.totalEstimate += issue.estimate;
        });
      })
    );

  count$: Observable<number> =
    this.items$.pipe(map((items: CrmIssueElastic[]) => items.length));

  constructor(private service: SubtaskLinkService) {
  }

  ngOnDestroy() {
    this.actions$.complete();
  }

  /**
   * Actions
   */
   load(id: number) {
    this.dispatch({type: 'load', payload: id});
  }

  store(parent: number, header: string, type: number) {
    this.dispatch({type: 'store', payload: {parent, header, type}});
  }

  private dispatch(action: SubtaskLinkAction): void {
    this.actions$.next(action);
  }
}

function ofType<T extends SubtaskLinkAction>(type: string): MonoTypeOperatorFunction<T> {
  return filter((_) => type === _.type);
}
